import Vue from 'vue';
import { Getter } from "vuex-class";
import Component from 'vue-class-component';
import { TranslationContext } from '@/types';
import { GenericCodeValue } from '@/store/types';

@Component
export class TranslationUtilsMixin extends Vue {
  @Getter('i18nEnabled', { namespace: 'features' }) private i18nEnabled!: boolean;
  @Getter('getDefaultLocale', { namespace: 'features' }) private getDefaultLocale!: string;
  @Getter('getAvailableLocales', { namespace: 'features' }) public getAvailableLocales!: string[];
  @Getter('ctrIposKidney', { namespace: 'features' }) public ctrIposKidney!: string[];

  get yesNoOptions(): GenericCodeValue[] {
    return [
      { code: "1", value: this.$t("yes").toString() },
      { code: "0", value: this.$t("no").toString() },
    ];
  }

  /**
   * Translate translationKey from Locale and update the organSharingProgram variable
   * to reflect HSP or IPOS depending on ctrIposKidney configuration
   * any translation key is permitted and will just return the actual translated text
   * if it don't have {organSharingProgram} variable.
   * @param translationKey translationKey to check on Locale
   * @returns {string} translation result
   */
  public translateIPOSContext(translationKey: string): string {
    const IposLabel = this.ctrIposKidney ? 'IPOS' : 'HSP';
    
    return this.$t(translationKey, { organSharingProgram: IposLabel }).toString();
  }

  /**
   * Translate context key as well as individually-translated values
   *
   * @param context object containing i18n translation key and optional template values
   * @returns {string} translation result
   */
  public translateContext(context: TranslationContext): string {
    const translatedValues: { [key: string]: string } = {};
    const contextValues = context.values || {};
    Object.keys(contextValues).forEach((contextValueKey: string) => {
      const contextValue = contextValues[contextValueKey];
      translatedValues[contextValueKey] = this.$te(contextValue) ? this.$t(contextValue).toString() : this.$t(contextValue).toString();
    });
    // Adds support for CTR IPOS Kidney Locale variable
    translatedValues['organSharingProgram'] = this.ctrIposKidney ? 'IPOS' : 'HSP';
    return this.$t(context.key, translatedValues).toString();
  }

  /**
   * Convenience method for array of contexts
   *
   * @param contexts array of objects containing i18n translation key and optional template values
   * @returns {string[]} translation results
   */
  public translateContexts(contexts: TranslationContext[]): string[] {
    const result = contexts.map((context: TranslationContext): string => {
      return this.translateContext(context);
    });
    return result;
  }

  /**
   * Handle translations for Wait Time
   *
   * @param waitTimeDays numeric value for Wait Time result, or null if no result
   * @returns {string} translation result
   */
  public translateWaitTime(waitTimeDays: number|null): string {
    // NOTE: here we use double-equals to ensure that zero 0 is NOT displayed as dash '-'
    if (waitTimeDays == null) return '-';

    return this.$tc('wait_time_days', waitTimeDays).toString();
  }

  public get currentLocale(): string {
    if (!this.i18nEnabled) { return this.getDefaultLocale; }

    return localStorage.getItem('locale') || this.getDefaultLocale;
  }

  setupI18n() {
    const defaultLocale = this.getDefaultLocale;
    const availableLocales = this.getAvailableLocales;

    const localeFromStorage = localStorage.getItem('locale');

    if (this.i18nEnabled) {
      // check for bad locale and replace with default
      if (localeFromStorage == 'undefined' || localeFromStorage == null || !availableLocales.includes(localeFromStorage)) {
        this.$i18n.locale = defaultLocale;
        localStorage.setItem('locale', defaultLocale);  
      } else {
        this.$i18n.locale = localeFromStorage;
      }
    } else {
      // if i18n disabled, overwrite current locale with default
      this.$i18n.locale = defaultLocale;
      localStorage.setItem('locale', defaultLocale);
    }
  }
}
